import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, ClipboardList, Scale, BookMarked, StickyNote, Calendar,
  Users, FolderOpen, Download, Phone, Search, MessageCircle, Star,
  Sparkles, X, Loader2, ChevronDown, ChevronUp,
} from 'lucide-react';
import ActionSlideOver from './ActionSlideOver';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ActionCenter — the persistent post-answer command bar.
 *
 * Shows 13 action tiles that mirror the landing page visual language.
 * The top N tiles (default 5) are prioritized based on `answer.primary_issue`
 * and `answer.confidence`. "More actions" reveals the rest.
 *
 * Actions that require a saved Case Locker case auto-create one first
 * via POST /api/case-locker/cases and then navigate to the correct tab.
 */
export default function ActionCenter({ question, answer, onFocusInput }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [modal, setModal] = useState(null); // { type, data }
  const [toast, setToast] = useState(null); // { kind, text }

  function showToast(kind, text, ms = 4000) {
    setToast({ kind, text });
    setTimeout(() => setToast(null), ms);
  }

  // Determine which actions to surface first based on the answer's shape.
  const priorityKeys = useMemo(() => prioritize(answer), [answer]);

  async function ensureCase() {
    // If we already saved this Q/A once this session, reuse the id.
    if (window.__lastAskStewieCaseId) return window.__lastAskStewieCaseId;
    const token = localStorage.getItem('token') || localStorage.getItem('access_token') || '';
    const today = new Date().toISOString().slice(0, 10);
    const verdict = answer?.verified_answer || answer?.key_takeaway || answer?.explanation || '';
    try {
      const res = await axios.post(
        `${API}/api/case-locker/cases`,
        {
          title: (question || 'Ask Stewie question').slice(0, 120),
          issue_type: mapIssueType(answer?.primary_issue),
          incident_date: today,
          summary: verdict.slice(0, 600),
          notes: `Question asked: "${question || ''}"`,
        },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const caseId = res?.data?.case?.case_id || res?.data?.case?.id;
      if (caseId) window.__lastAskStewieCaseId = caseId;
      return caseId;
    } catch (e) {
      console.warn('ensureCase failed:', e?.response?.status, e?.message);
      return null;
    }
  }

  function scrollTo(selectors) {
    for (const sel of Array.isArray(selectors) ? selectors : [selectors]) {
      const el = document.querySelector(sel);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.classList.add('vac-pulse-ring');
        setTimeout(() => el.classList.remove('vac-pulse-ring'), 1600);
        return true;
      }
    }
    return false;
  }

  async function handleAction(key) {
    setBusyKey(key);
    try {
      switch (key) {
        case 'grievance':
          navigate('/grievance-builder', { state: { question, answer, from: 'ask-stewie' } });
          break;
        case 'factfind':
          navigate('/fact-finder', { state: { question, answer, from: 'ask-stewie' } });
          break;
        case 'contract':
          scrollTo(['[data-testid="verified-answer-source"]']);
          break;
        case 'sources':
          scrollTo(['[data-testid="sources-panel"]']);
          break;
        case 'followup':
          onFocusInput?.();
          break;
        case 'save':
          {
            const id = await ensureCase();
            if (id) navigate(`/case-locker/${id}`);
            else { showToast('error', 'Could not save case. Opening the Case Locker so you can create one manually.'); navigate('/case-locker'); }
          }
          break;
        case 'timeline':
        case 'export':
          {
            const id = await ensureCase();
            const hash = { timeline: '#timeline', export: '#export' }[key] || '';
            if (id) navigate(`/case-locker/${id}${hash}`);
            else { showToast('error', "Couldn't auto-create a case. Opening the Case Locker so you can add this to an existing one."); navigate(`/case-locker${hash}`); }
          }
          break;
        case 'witnesses':
        case 'evidence':
        case 'stewardnotes':
          {
            const id = await ensureCase();
            if (id) setModal({ type: key, caseId: id });
            else { showToast('error', "Couldn't auto-create a case. Try again or open the Case Locker."); }
          }
          break;
        case 'contact':
          setModal({ type: 'contact' });
          break;
        case 'similar':
          navigate(`/case-locker?similar=${encodeURIComponent(String(answer?.primary_issue || ''))}`);
          break;
        default:
          break;
      }
    } finally {
      setBusyKey(null);
    }
  }

  const visibleKeys = expanded ? ALL_ACTIONS.map(a => a.key) : priorityKeys;
  const visible = visibleKeys.map(k => ALL_ACTIONS.find(a => a.key === k)).filter(Boolean);

  return (
    <div className="ac-root" data-testid="action-center">
      <style>{styles}</style>

      <div className="ac-head">
        <span className="ac-eyebrow">
          <Sparkles size={12} strokeWidth={2.4} />
          NEXT STEPS
        </span>
        <button
          type="button"
          className="ac-more"
          onClick={() => setExpanded(e => !e)}
          data-testid="action-center-toggle"
        >
          {expanded ? <>Show less <ChevronUp size={12} /></> : <>More actions <ChevronDown size={12} /></>}
        </button>
      </div>

      <div className={`ac-grid ${expanded ? 'ac-grid--all' : 'ac-grid--priority'}`}>
        {visible.map((a) => (
          <ActionTile
            key={a.key}
            a={a}
            busy={busyKey === a.key}
            onClick={() => handleAction(a.key)}
          />
        ))}
      </div>

      {modal?.type === 'contact' && (
        <ContactStewardModal onClose={() => setModal(null)} />
      )}

      {(modal?.type === 'stewardnotes' || modal?.type === 'witnesses' || modal?.type === 'evidence') && (
        <ActionSlideOver
          type={modal.type}
          caseId={modal.caseId}
          onClose={() => setModal(null)}
          onResult={({ ok, text }) => showToast(ok ? 'info' : 'error', text)}
        />
      )}

      {toast && (
        <div
          className={`ac-toast ac-toast-${toast.kind}`}
          data-testid="action-center-toast"
          role="alert"
        >
          {toast.text}
          <button
            type="button"
            className="ac-toast-close"
            onClick={() => setToast(null)}
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function ActionTile({ a, busy, onClick }) {
  return (
    <button
      type="button"
      className="ac-tile"
      onClick={onClick}
      data-testid={`action-${a.key}`}
      disabled={busy}
      style={{ '--ac-c': a.color, '--ac-cs': a.colorSoft }}
      aria-label={a.title}
    >
      <span className="ac-tile-icon">
        {busy ? <Loader2 size={20} className="ac-spin" strokeWidth={2.2} />
             : <a.Icon size={20} strokeWidth={2.2} />}
      </span>
      <span className="ac-tile-text">
        <span className="ac-tile-emoji" aria-hidden="true">{a.emoji}</span>
        <span className="ac-tile-title">{a.title}</span>
      </span>
    </button>
  );
}

function ContactStewardModal({ onClose }) {
  return (
    <div className="ac-modal-overlay" onClick={onClose} data-testid="contact-steward-modal">
      <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ac-modal-head">
          <span className="ac-modal-title">📞 Contact Your Steward</span>
          <button type="button" className="ac-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="ac-modal-body">
          <p className="ac-modal-lead">
            AskStewie will surface your assigned Local 15 steward here as soon as your unit and
            classification are on file. For now, use the direct contacts below.
          </p>
          <div className="ac-contact-row">
            <div className="ac-contact-item">
              <span className="ac-contact-lbl">LOCAL 15 HALL</span>
              <a className="ac-contact-val" href="tel:+16308393005">(630) 839-3005</a>
            </div>
            <div className="ac-contact-item">
              <span className="ac-contact-lbl">EMAIL</span>
              <a className="ac-contact-val" href="mailto:info@ibewlocal15.org">info@ibewlocal15.org</a>
            </div>
          </div>
          <p className="ac-modal-note">
            Not sure who your steward is? Ask your foreman, check your union bulletin board,
            or call the Hall — they&apos;ll route you.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Actions registry ─────────────────────────────────────────────── */
const ALL_ACTIONS = [
  { key: 'grievance',    emoji: '📝', title: 'File a Grievance',       Icon: FileText,       color: '#ef4444', colorSoft: 'rgba(239,68,68,0.10)' },
  { key: 'factfind',     emoji: '📋', title: 'Prepare for Fact Finding', Icon: ClipboardList,color: '#f59e0b', colorSoft: 'rgba(245,158,11,0.12)' },
  { key: 'contract',     emoji: '⚖️', title: 'Contract Language',      Icon: Scale,          color: '#22d3ee', colorSoft: 'rgba(34,211,238,0.12)' },
  { key: 'sources',      emoji: '📚', title: 'Supporting Documents',   Icon: BookMarked,     color: '#60a5fa', colorSoft: 'rgba(96,165,250,0.12)' },
  { key: 'stewardnotes', emoji: '📄', title: 'Generate Steward Notes', Icon: StickyNote,     color: '#a78bfa', colorSoft: 'rgba(167,139,250,0.12)' },
  { key: 'timeline',     emoji: '📅', title: 'Timeline Builder',       Icon: Calendar,       color: '#22c55e', colorSoft: 'rgba(34,197,94,0.12)' },
  { key: 'witnesses',    emoji: '👥', title: 'Witness Statements',     Icon: Users,          color: '#c084fc', colorSoft: 'rgba(192,132,252,0.12)' },
  { key: 'evidence',     emoji: '📂', title: 'Evidence Locker',        Icon: FolderOpen,     color: '#facc15', colorSoft: 'rgba(250,204,21,0.12)' },
  { key: 'export',       emoji: '📤', title: 'Export Case',            Icon: Download,       color: '#38bdf8', colorSoft: 'rgba(56,189,248,0.12)' },
  { key: 'contact',      emoji: '📞', title: 'Contact Your Steward',   Icon: Phone,          color: '#fb923c', colorSoft: 'rgba(251,146,60,0.12)' },
  { key: 'similar',      emoji: '🔍', title: 'Find Similar Cases',     Icon: Search,         color: '#5eead4', colorSoft: 'rgba(94,234,212,0.12)' },
  { key: 'followup',     emoji: '❓', title: 'Ask a Follow-Up',        Icon: MessageCircle,  color: '#67e8f9', colorSoft: 'rgba(103,232,249,0.12)' },
  { key: 'save',         emoji: '⭐', title: 'Save to My Cases',       Icon: Star,           color: '#fde047', colorSoft: 'rgba(253,224,71,0.12)' },
];

/**
 * Rank actions by relevance to the answer, then return the top 5 keys.
 * Contract-grounded, discipline, and grievance-heavy issues promote different tiles.
 */
function prioritize(answer) {
  const primary = String(answer?.primary_issue || '').toLowerCase();
  const mode = String(answer?.mode || answer?.response_mode || '').toLowerCase();
  const conf = answer?.confidence_percent ?? Math.round((answer?.confidence || 0) * 100);
  const hasGrievanceLang = !!(answer?.grievance_language && String(answer.grievance_language).trim().length > 40);

  // Base ranking; higher = more relevant.
  const score = {
    grievance:    hasGrievanceLang ? 100 : 70,
    factfind:     40,
    contract:     mode === 'contract_grounded' ? 90 : 55,
    sources:      75,
    stewardnotes: 60,
    timeline:     50,
    witnesses:    30,
    evidence:     45,
    export:       25,
    contact:      35,
    similar:      45,
    followup:     conf < 60 ? 95 : 55,   // clarify first if confidence is low
    save:         85,
  };

  // Issue-specific boosts.
  if (primary.includes('discipline') || primary.includes('discharge') || primary.includes('termination')) {
    score.factfind += 40; score.witnesses += 30; score.timeline += 20; score.stewardnotes += 25;
  }
  if (primary.includes('overtime') || primary.includes('mandatory')) {
    score.grievance += 15; score.timeline += 10;
  }
  if (primary.includes('safety') || primary.includes('injury')) {
    score.witnesses += 25; score.evidence += 30;
  }
  if (primary.includes('weingarten') || primary.includes('investigation')) {
    score.factfind += 50; score.witnesses += 20;
  }

  // Sort by score desc and return top 5 keys, guaranteeing Save + Follow-up show.
  const sorted = ALL_ACTIONS
    .map(a => ({ key: a.key, s: score[a.key] || 0 }))
    .sort((x, y) => y.s - x.s)
    .map(x => x.key);
  const top = sorted.slice(0, 5);
  if (!top.includes('save')) top.splice(4, 1, 'save');
  if (!top.includes('followup')) top.splice(3, 1, 'followup');
  return top;
}

function mapIssueType(slug) {
  const map = {
    overtime_bypass: 'overtime_dispute', overtime: 'overtime_dispute', mandatory_overtime: 'overtime_dispute',
    discipline: 'discipline_investigation', discharge: 'discipline_investigation', termination: 'discipline_investigation',
    weingarten: 'weingarten_violation', weingarten_violation: 'weingarten_violation',
    seniority: 'seniority_dispute', seniority_dispute: 'seniority_dispute',
    contracting: 'contractor_violation', contracting_out: 'contractor_violation', contractor_violations: 'contractor_violation',
    safety: 'safety_concern', safety_violation: 'safety_concern',
    injury_near_miss: 'injury_near_miss',
    harassment: 'harassment_conduct', harassment_conduct: 'harassment_conduct',
    pay_wage_issue: 'pay_wage_issue', benefits_pay: 'pay_wage_issue', pay_wages: 'pay_wage_issue',
    scheduling: 'scheduling_issue', scheduling_issue: 'scheduling_issue',
    drug_alcohol: 'discipline_investigation',
    contract_violation: 'contract_violation',
    promotion: 'promotion_dispute', promotion_dispute: 'promotion_dispute',
    recall: 'recall_dispute', recall_dispute: 'recall_dispute',
    vacation_pay: 'vacation_pay_issue', vacation_pay_issue: 'vacation_pay_issue',
    holiday_pay: 'holiday_pay_issue', holiday_pay_issue: 'holiday_pay_issue',
    working_conditions: 'working_conditions',
    staffing: 'staffing_dispute', staffing_dispute: 'staffing_dispute',
  };
  return map[String(slug || '').toLowerCase()] || 'other';
}

/* ── Styles — matches landing tile aesthetics ──────────────────────── */
const styles = `
.ac-root {
  margin: 8px 0 14px;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", system-ui, sans-serif;
}

.ac-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 8px; margin-bottom: 10px;
}
.ac-eyebrow {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.14em;
  color: #facc15; text-transform: uppercase;
}
.ac-eyebrow svg { color: #facc15; }
.ac-more {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  background: rgba(34,211,238,0.08);
  border: 1px solid rgba(34,211,238,0.35);
  border-radius: 999px;
  color: #67e8f9;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em;
  cursor: pointer;
  transition: background 150ms, border-color 150ms;
}
.ac-more:hover { background: rgba(34,211,238,0.16); border-color: rgba(34,211,238,0.55); }

/* Grid — matches landing quick-tiles + example-cards */
.ac-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}
@media (min-width: 640px) {
  .ac-grid--priority { grid-template-columns: repeat(3, 1fr); }
  .ac-grid--all      { grid-template-columns: repeat(3, 1fr); }
}
@media (min-width: 1024px) {
  .ac-grid--priority { grid-template-columns: repeat(5, 1fr); }
  .ac-grid--all      { grid-template-columns: repeat(4, 1fr); }
}

.ac-tile {
  position: relative;
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px 10px 10px;
  min-height: 88px;
  background: rgba(15, 27, 70, 0.75);
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  color: #f1f5f9;
  cursor: pointer;
  text-align: left;
  transition: transform 160ms cubic-bezier(0.16,1,0.3,1),
              border-color 160ms cubic-bezier(0.16,1,0.3,1),
              background 160ms;
}
.ac-tile:hover:not(:disabled) {
  transform: translateY(-2px);
  border-color: var(--ac-c);
  background: rgba(15, 27, 70, 0.9);
}
.ac-tile:active:not(:disabled) { transform: translateY(0); }
.ac-tile:disabled { opacity: 0.55; cursor: wait; }

.ac-tile-icon {
  width: 34px; height: 34px;
  border-radius: 10px;
  background: var(--ac-cs);
  border: 1px solid var(--ac-c);
  color: var(--ac-c);
  display: grid; place-items: center;
  flex-shrink: 0;
}

.ac-tile-text { display: flex; align-items: center; gap: 6px; }
.ac-tile-emoji { font-size: 13px; line-height: 1; }
.ac-tile-title {
  font-size: 12px; font-weight: 700;
  line-height: 1.25;
  color: #f1f5f9;
  letter-spacing: -0.005em;
}

.ac-spin { animation: ac-spin-kf 1.2s linear infinite; }
@keyframes ac-spin-kf { to { transform: rotate(360deg); } }

/* Pulse ring applied to scroll-to targets */
@keyframes vac-pulse-kf {
  0%   { box-shadow: 0 0 0 0 rgba(34,211,238,0.55); }
  50%  { box-shadow: 0 0 0 4px rgba(34,211,238,0.40), 0 0 24px 4px rgba(34,211,238,0.32); }
  100% { box-shadow: 0 0 0 0 rgba(34,211,238,0.0); }
}
.vac-pulse-ring { animation: vac-pulse-kf 1.6s ease-out; border-radius: 12px; }

/* Contact steward modal */
.ac-modal-overlay {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(6, 17, 31, 0.72);
  backdrop-filter: blur(10px);
  display: grid; place-items: center;
  padding: 20px;
  animation: ac-fade 200ms ease-out both;
}
@keyframes ac-fade { from { opacity: 0; } to { opacity: 1; } }
.ac-modal {
  width: 100%; max-width: 460px;
  background: #0d1b2e;
  border: 1px solid rgba(148,163,184,0.28);
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.55);
}
.ac-modal-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(148,163,184,0.14);
}
.ac-modal-title { color: #f1f5f9; font-size: 16px; font-weight: 800; letter-spacing: -0.01em; }
.ac-modal-close {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 10px;
  width: 32px; height: 32px;
  display: grid; place-items: center;
  color: #94a3b8; cursor: pointer;
}
.ac-modal-close:hover { background: rgba(255,255,255,0.10); color: #f1f5f9; }
.ac-modal-body { padding: 16px; }
.ac-modal-lead { color: #cbd5e1; font-size: 13.5px; line-height: 1.55; margin: 0 0 12px; }
.ac-contact-row {
  display: grid; grid-template-columns: 1fr; gap: 8px;
  padding: 12px; background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
}
@media (min-width: 500px) { .ac-contact-row { grid-template-columns: 1fr 1fr; } }
.ac-contact-item { display: flex; flex-direction: column; gap: 2px; }
.ac-contact-lbl {
  font-size: 10px; font-weight: 800; letter-spacing: 0.14em;
  color: #94a3b8; text-transform: uppercase;
}
.ac-contact-val {
  color: #67e8f9; font-size: 15px; font-weight: 700;
  text-decoration: none;
}
.ac-contact-val:hover { color: #a5f3fc; text-decoration: underline; }
.ac-modal-note {
  color: #94a3b8; font-size: 12px; line-height: 1.5;
  margin: 12px 0 0;
}

/* Inline toast (case-creation failures / status messages) */
.ac-toast {
  position: fixed;
  bottom: 24px; left: 50%;
  transform: translateX(-50%);
  z-index: 120;
  max-width: min(92vw, 460px);
  padding: 12px 40px 12px 14px;
  border-radius: 12px;
  font-size: 13px; font-weight: 600; line-height: 1.4;
  box-shadow: 0 12px 32px rgba(0,0,0,0.4);
  animation: ac-toast-in 200ms ease-out;
}
@keyframes ac-toast-in {
  from { opacity: 0; transform: translate(-50%, 12px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
}
.ac-toast-error {
  background: rgba(239,68,68,0.14);
  border: 1px solid rgba(239,68,68,0.55);
  color: #fecaca;
}
.ac-toast-info {
  background: rgba(96,165,250,0.14);
  border: 1px solid rgba(96,165,250,0.55);
  color: #bfdbfe;
}
.ac-toast-close {
  position: absolute; top: 8px; right: 8px;
  background: transparent; border: none;
  color: inherit; cursor: pointer;
  padding: 4px; opacity: 0.7;
  display: grid; place-items: center;
}
.ac-toast-close:hover { opacity: 1; }
`;
